/**
 * Motion System Utilities
 * 
 * Centralized motion variants and constants for Framer Motion animations.
 * All animations use transform and opacity only for GPU acceleration.
 * Respects prefers-reduced-motion via Framer Motion's built-in support.
 */

// Easing constants matching PRD specs
export const easing = {
  primary: [0.22, 1, 0.36, 1], // cubic-bezier(0.22, 1, 0.36, 1)
  secondary: [0.4, 0, 0.2, 1], // cubic-bezier(0.4, 0, 0.2, 1)
};

// Duration constants (in seconds)
export const duration = {
  micro: 0.12, // 120ms
  microAlt: 0.18, // 180ms
  hover: 0.18, // 180ms
  hoverAlt: 0.22, // 220ms
  section: 0.4, // 400ms
  sectionAlt: 0.6, // 600ms
  pageEnter: 0.5, // 500ms
  pageExit: 0.35, // 350ms
};

/**
 * Page transition variants for route-level animations
 * Exit: opacity 1→0, translateY 0→-8px, 350ms
 * Enter: opacity 0→1, translateY 16px→0, 500ms
 */
export const pageTransition = {
  initial: {
    opacity: 0,
    y: 16,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.pageEnter,
      ease: easing.primary,
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: duration.pageExit,
      ease: easing.primary,
    },
  },
};

/**
 * Section reveal animation
 * Triggered when section enters 75% viewport
 * Opacity: 0 → 1, TranslateY: 24px → 0, Duration: 600ms
 */
export const sectionReveal = {
  hidden: {
    opacity: 0,
    y: 24,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.sectionAlt,
      ease: easing.primary,
    },
  },
};

/**
 * Container variant for staggered children animations
 * Use with staggerChildren prop
 */
export const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12, // 120ms stagger between children
      delayChildren: 0,
    },
  },
};

/**
 * Standard fade + translateY animation
 * Used for individual elements within staggered containers
 * Supports custom delay via custom prop
 */
export const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: (custom = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.sectionAlt,
      ease: easing.primary,
      delay: custom * 0.08, // 80ms stagger per item
    },
  }),
};

/**
 * Hero text hierarchy reveal
 * Staggered text animations for headline, subheadline, CTA
 */
export const heroTextReveal = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: duration.sectionAlt,
      ease: easing.primary,
    },
  },
};

/**
 * CTA section entry animation
 * Scale: 0.96 → 1, Opacity: 0 → 1, Duration: 500ms
 */
export const ctaEntry = {
  hidden: {
    opacity: 0,
    scale: 0.96,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: duration.pageEnter,
      ease: easing.primary,
    },
  },
};

/**
 * Sticky header animation
 * translateY: -100% → 0, Duration: 300ms
 */
export const stickyHeader = {
  hidden: {
    y: '-100%',
  },
  visible: {
    y: 0,
    transition: {
      duration: 0.3,
      ease: easing.secondary,
    },
  },
};

